// Flashcard drill over an entire HSK level's word list (independent of the
// user's Library — this is the "review everything" mode, not the "review
// what I saved" mode Library owns). Shuffled once per session; Space to
// reveal, J/F correct, K/D wrong, Escape to exit.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Plus, RotateCcw, X } from 'lucide-react';
import type { HskReviewLevel, HskVocabItem } from '@/lib/hsk-review/types';
import { loadHskVocab } from '@/lib/hsk-review/data';
import type { LibraryWord, Word } from '@/lib/types';
import { Events, track } from '@/lib/observability';
import { SealStamp } from '@/components/visual/SealStamp';
import { InkMountains } from '@/components/visual/InkMountains';
import { C } from '@/styles/tokens';

type RevealStage = 'hidden' | 'pinyin' | 'full';

interface HSKReviewFlashcardsProps {
  level: HskReviewLevel;
  library: LibraryWord[];
  onClose: () => void;
  onSaveWord: (word: Word) => void;
}

function shuffle<T>(items: T[]): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

export function HSKReviewFlashcards({
  level,
  library,
  onClose,
  onSaveWord,
}: HSKReviewFlashcardsProps) {
  const [vocab, setVocab] = useState<HskVocabItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [stage, setStage] = useState<RevealStage>('hidden');
  const cardShownAt = useRef<number>(Date.now());

  useEffect(() => {
    let cancelled = false;
    loadHskVocab(level)
      .then((data) => {
        if (cancelled) return;
        setVocab(shuffle(data));
        track(Events.FlashcardSession, { source: 'hsk_review', level, size: data.length });
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, [level]);

  const librarySet = useMemo(() => new Set(library.map((l) => l.hz)), [library]);
  const card = vocab ? vocab[idx] : null;

  useEffect(() => {
    cardShownAt.current = Date.now();
  }, [idx]);

  function next() {
    if (!vocab) return;
    if (idx + 1 >= vocab.length) {
      onClose();
      return;
    }
    setStage('hidden');
    setIdx((i) => i + 1);
  }

  function recordAttempt(correct: boolean) {
    if (!card) return;
    track(Events.FlashcardAttempt, {
      hz: card.hz,
      correct,
      latencyMs: Date.now() - cardShownAt.current,
      unaided: stage === 'hidden',
      source: 'hsk_review',
      level,
    });
  }

  function flipStage() {
    setStage((s) => (s === 'hidden' ? 'pinyin' : s === 'pinyin' ? 'full' : 'hidden'));
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === ' ') {
        e.preventDefault();
        flipStage();
      } else if (e.key === 'j' || e.key === 'J' || e.key === 'f' || e.key === 'F') {
        recordAttempt(true);
        next();
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'd' || e.key === 'D') {
        recordAttempt(false);
        next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, stage, vocab]);

  if (error) {
    return (
      <div
        role="dialog"
        aria-modal
        aria-label="Flashcards"
        className="fixed inset-0 z-40 flex items-center justify-center"
        style={{ background: C.cream }}
      >
        <div className="text-center px-6">
          <p className="ff-display text-2xl mb-2" style={{ color: C.ink }}>Couldn&apos;t load HSK {level}</p>
          <p className="ff-body text-[13px] mb-4" style={{ color: C.inkLight }}>{error}</p>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-full text-white"
            style={{ background: C.vermillion }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!vocab) {
    return (
      <div
        role="dialog"
        aria-modal
        aria-label="Flashcards"
        className="fixed inset-0 z-40 flex items-center justify-center gap-2 ff-body text-[13px]"
        style={{ background: C.paper, color: C.inkLight }}
      >
        <Loader2 size={14} className="animate-spin" aria-hidden /> Loading HSK {level}…
      </div>
    );
  }

  if (!card) {
    return (
      <div
        role="dialog"
        aria-modal
        aria-label="Flashcards"
        className="fixed inset-0 z-40 flex items-center justify-center"
        style={{ background: C.cream }}
      >
        <div className="text-center">
          <p className="ff-display text-3xl mb-1" style={{ color: C.ink }}>Nothing to drill</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 px-5 py-2 rounded-full text-white"
            style={{ background: C.vermillion }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const showPinyin = stage !== 'hidden';
  const showEn = stage === 'full';
  const alreadySaved = librarySet.has(card.hz);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label="HSK flashcards"
      className="fixed inset-0 z-40 flex flex-col overflow-hidden"
      style={{ background: C.paper }}
    >
      <header className="grid grid-cols-[40px_1fr_40px] items-start px-4 py-3 safe-top relative">
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          aria-label="Close flashcards"
          style={{ background: 'white', boxShadow: '0 1px 4px rgba(26,22,20,0.06)' }}
        >
          <X size={18} aria-hidden />
        </button>
        <div className="text-center -mt-1">
          <div className="ff-zh-serif font-bold text-3xl leading-none" style={{ color: C.ink }}>
            HSK {level}
          </div>
          <div className="ff-body text-[12px] mt-1" style={{ color: C.inkLight }}>
            {idx + 1} / {vocab.length}
          </div>
        </div>
        <span aria-hidden />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-5 safe-bottom">
        <button
          type="button"
          onClick={flipStage}
          className="relative w-full max-w-[380px] aspect-square rounded-3xl flex flex-col items-center justify-center text-center px-6 anim-scale-in overflow-hidden shadow-paper-lg"
          style={{ background: C.cream, border: `1px solid ${C.mist}` }}
          aria-label={
            stage === 'hidden'
              ? 'Reveal pinyin'
              : stage === 'pinyin'
                ? 'Show translation'
                : 'Hide pinyin and translation'
          }
        >
          <SealStamp char="习" size={32} className="absolute top-3 right-3" />
          <span
            className="ff-zh-serif font-bold leading-none"
            style={{ color: C.ink, fontSize: 'clamp(64px, 20vw, 100px)' }}
          >
            {card.hz}
          </span>
          {showPinyin && (
            <div className="mt-5 anim-slide-up">
              <div className="ff-body text-base opacity-80" style={{ color: C.ink }}>
                {card.py}
              </div>
              {showEn && (
                <div className="ff-body text-sm italic mt-1 max-w-[300px]" style={{ color: C.inkLight }}>
                  {card.en}
                </div>
              )}
            </div>
          )}
          {stage === 'hidden' && (
            <span
              className="mt-6 ff-body text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
              style={{ color: C.inkSofter }}
            >
              <span aria-hidden style={{ color: C.vermillion, fontSize: 10 }}>◇</span>
              Tap for pinyin
            </span>
          )}
          {stage === 'pinyin' && (
            <span
              className="mt-4 ff-body text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
              style={{ color: C.inkSofter }}
            >
              <span aria-hidden style={{ color: C.vermillion, fontSize: 10 }}>◇</span>
              Tap for translation
            </span>
          )}
          <InkMountains height={64} className="absolute left-0 right-0 bottom-0" tint={C.ink} />
        </button>

        {/* Save-to-library link, so a card you don't know can be captured
            immediately rather than requiring a trip back to the browse tab. */}
        <button
          type="button"
          disabled={alreadySaved}
          onClick={() => {
            onSaveWord({ hz: card.hz, py: card.py, en: card.en });
            track(Events.WordFirstEncountered, { hz: card.hz, source: 'hsk_flashcard' });
            track(Events.WordSaved, { hz: card.hz, source: 'hsk_flashcard', level });
          }}
          className="mt-3 ff-body text-[11px] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5 px-3 py-1 rounded-full disabled:opacity-60"
          style={{
            background: alreadySaved ? C.blush : 'white',
            color: alreadySaved ? C.vermillionDeep : C.ink,
            border: `1px solid ${alreadySaved ? C.blush : C.mist}`,
          }}
        >
          {alreadySaved ? (
            <>
              <Check size={11} aria-hidden /> In Library
            </>
          ) : (
            <>
              <Plus size={11} aria-hidden /> Add to Library
            </>
          )}
        </button>

        <div className="w-full max-w-[380px] mt-4 flex gap-3">
          <button
            type="button"
            onClick={() => {
              recordAttempt(false);
              next();
            }}
            className="flex-1 py-2.5 rounded-2xl flex flex-col items-center justify-center gap-0.5"
            style={{
              background: 'white',
              color: C.ink,
              border: `1px solid ${C.blush}`,
              boxShadow: '0 1px 3px rgba(26,22,20,0.04)',
            }}
          >
            <span className="ff-body font-semibold text-[14px] inline-flex items-center gap-1.5 leading-none">
              <RotateCcw size={13} aria-hidden style={{ color: C.gold }} />
              Still learning
            </span>
            <span className="ff-zh-serif text-[11px] leading-none" style={{ color: C.inkLight }}>
              还在学
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              recordAttempt(true);
              next();
            }}
            className="flex-1 py-2.5 rounded-2xl flex flex-col items-center justify-center gap-0.5 text-white"
            style={{ background: C.jade, boxShadow: '0 4px 12px rgba(92,138,107,0.30)' }}
          >
            <span className="ff-body font-semibold text-[14px] inline-flex items-center gap-1.5 leading-none">
              <Check size={13} aria-hidden />
              I know this
            </span>
            <span className="ff-zh-serif text-[11px] leading-none opacity-90">
              我会了
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
